// Importing React and PropTypes
import React from 'react'
import PropTypes from 'prop-types'
import DangerousInnerHTML from 'portal-modules/common/components/DangerousInnerHTML'
// Importing Utility Functions 
import { formatDate } from 'portal-modules/common/utils/auto-payment-utils'
// Importing constant
import { AUTOPAY_TYPES } from 'portal-modules/payments/constants'
// Importing Styled Components
import {
  StyledDetailsP,
  StyledDetailsH5,
  StyledSection,
  StyledDetailsDiv,
  StyledMaxPayment,
  StyledDetailWrapper,
  StyledBold,
  StyledDetailsBiMonthlyCardContainer,
  StyledDetailsBiMonthlySection,
  StyledDetailSplitCardContainer,
  StyledDetailsSplitSection,
} from './atoms'

const getTotalAmount = payments =>
  payments &&
  payments.reduce((initial_amount, { payment_amount }) => {
    return payment_amount ? initial_amount + payment_amount : initial_amount
  }, 0)

const EndDate = ({ endDate, type }) => {
  let text = ''
  switch (endDate) {
    case 0:
      text = __('<strong>Payments End</strong> <br> <span>When I Cancel</span>')
      break
    case 1:
      text = __(
        '<strong>Payments End</strong> <br> <span>When I Cancel or Move Out</span>',
      )
      break
    default:
      text = __(
        '<strong>Payments End</strong> <br> <span>{%t, 0, MMM YYYY}</span>',
        [new Date(endDate * 1000)],
      )
      break
  }
  return (
    <StyledDetailsDiv>
      <StyledDetailsH5 type={type}>
        <DangerousInnerHTML>{text}</DangerousInnerHTML>
      </StyledDetailsH5>
    </StyledDetailsDiv>
  )
}

EndDate.propTypes = {
  endDate: PropTypes.number,
}

export const AutoPayment = ({ 
  end_date, 
  start_date,
  is_variable_amount,
  max_payment_amount,
}) => {
  // Get the end date text
  const getEndDateText = () => {
    switch (end_date) {
      case 0:
        return __('{%t, 0, Do} of Each Month - Ends: When I Cancel', [start_date])
      case 1:
        return __('{%t, 0, Do} of Each Month - Ends: When I Cancel or Move Out', [start_date])
      default:
        return __('{%t, 0, Do} of Each Month - Ends: {%t, 1, MMM YYYY}', [
          start_date,
          new Date(end_date * 1000).toISOString().split('T')[0],
        ])
    }
  }

  // Split the end date text into payment date and end date
  const [paymentDate, endDate] = getEndDateText().split(' - ')
  // Format the start date
  const formattedStartDate = formatDate(start_date, 'DATE_ALPHA_POSTMONTH')
  
  return (
    <StyledSection>
      {is_variable_amount && max_payment_amount ? (
        <StyledMaxPayment>
          ({i18nUtils.currency(max_payment_amount)} {__('maximum payment')})
        </StyledMaxPayment>
      ) : null}
      <StyledDetailWrapper>
        <StyledDetailsP>
          <span>{paymentDate}</span>
        </StyledDetailsP>
        {formattedStartDate && (
          <StyledDetailsP>
            <span>{`${__('Starts:')} ${formattedStartDate}`}</span>
          </StyledDetailsP>
        )}
        <StyledDetailsP>
          <span>{endDate}</span>
        </StyledDetailsP>
      </StyledDetailWrapper>
    </StyledSection>
  )
}

export const BiMonthly = ({ end_date, first_payment, second_payment }) => {  // Format the start date
  const formattedStartDate = formatDate(first_payment.start_date, 'DATE_ALPHA_POSTMONTH')
  return (
  <StyledDetailsBiMonthlyCardContainer>
    <StyledDetailsBiMonthlySection>
      <StyledDetailsH5>
        <StyledBold>{__('1st Payment')}</StyledBold>
        <span>
          {i18nUtils.datetime(first_payment.start_date, 'Do')} -
          {i18nUtils.currency(first_payment.max_payment_amount)}
        </span>
      </StyledDetailsH5>
      <StyledDetailsH5>
        <StyledBold>{__('Payments Start')}</StyledBold>
        {
          formattedStartDate && (
            <span>
              {formattedStartDate}
            </span> 
          )
        }
      </StyledDetailsH5>
    </StyledDetailsBiMonthlySection>
    <StyledDetailsBiMonthlySection>
      <StyledDetailsH5>
        <StyledBold>{__('2nd Payment')}&nbsp;</StyledBold>
        <span>
          {i18nUtils.datetime(second_payment.start_date, 'Do')} -
          {i18nUtils.currency(second_payment.max_payment_amount)}
        </span>
      </StyledDetailsH5>
      <EndDate endDate={end_date} type={AUTOPAY_TYPES.BIMONTHLY}/>
    </StyledDetailsBiMonthlySection>
  </StyledDetailsBiMonthlyCardContainer>
)}

export const Split = ({
  end_date,
  start_date,
  payment_amount,
  roommate_payments,
}) => { 
  const formattedStartDate = formatDate(start_date, 'DATE_ALPHA_POSTMONTH')

  return (
  <StyledDetailSplitCardContainer>
    <StyledDetailsBiMonthlySection>
      <StyledDetailsH5>
        <DangerousInnerHTML>
          {__(
            '<strong>Your Payment</strong> <br><span>{%m, 0} / {%m, 1}</span>',
            [payment_amount, getTotalAmount(roommate_payments)],
          )}
        </DangerousInnerHTML>
      </StyledDetailsH5>
      <StyledDetailsH5>
        <StyledBold>{__('Payments Start')}</StyledBold>
        {
          formattedStartDate && (
            <span>
              {formattedStartDate}
            </span> 
          )
        }
      </StyledDetailsH5>
    </StyledDetailsBiMonthlySection>
    <StyledDetailsBiMonthlySection>
      <StyledDetailsH5>
        <DangerousInnerHTML>
          {__(
            '<strong>Payment Day</strong> <br> <span>{%t, 0, Do} of the month</span>',
            [start_date],
          )}
        </DangerousInnerHTML>
      </StyledDetailsH5>
      <EndDate endDate={end_date} type={AUTOPAY_TYPES.SPLIT}/>
    </StyledDetailsBiMonthlySection>
    
  </StyledDetailSplitCardContainer>
)}

Split.propTypes = {
  end_date: PropTypes.number,
  start_date: PropTypes.string,
  payment_amount: PropTypes.number,
  roommate_payments: PropTypes.arrayOf(PropTypes.object),
}

AutoPayment.propTypes = {
  end_date: PropTypes.number,
  start_date: PropTypes.string,
  is_variable_amount: PropTypes.bool,
  max_payment_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

BiMonthly.propTypes = {
  end_date: PropTypes.number,
  first_payment: PropTypes.object,
  second_payment: PropTypes.object,
}
